import type { JSX } from 'react';

import type { FachgruppenSummen } from '@asas/domain';

import { wochenkopf } from '../wochenraster/wochenraster';

import { externAnzeige, summenAnzeige } from './auslastung';
import type { SummenAnzeige } from './auslastung';

/**
 * Die Darstellung von „Auslastung je Fachgruppe" (AC-407) — ohne Zustand und
 * ohne Netz. Je Fachgruppe eine Summenzeile (nie ausgeblendet, AK 4), darunter
 * die hervorgehobene Gesamtzeile und, abgesetzt, die Externen (AK 6). Ueber
 * Personen verlaesst keine Zahl den Server unter der Schwelle; hier steht dann
 * nur „unter N Personen" (AK 2). Ueberlast steht auch in Worten, nicht nur in
 * einer Farbe.
 */

export interface AuslastungTabelleProps {
  readonly summen: FachgruppenSummen;
}

function Zelle({ anzeige }: { readonly anzeige: SummenAnzeige }): JSX.Element {
  return (
    <td data-zustand={anzeige.zustand} title={anzeige.title === '' ? undefined : anzeige.title}>
      {anzeige.text}
      {anzeige.hinweis === '' ? null : (
        <>
          {' '}
          <small>({anzeige.hinweis})</small>
        </>
      )}
    </td>
  );
}

export function AuslastungTabelle({ summen }: AuslastungTabelleProps): JSX.Element {
  const { schwelle } = summen;

  return (
    <table>
      <caption>Planstunden / verfügbare Stunden und Auslastung je Fachgruppe und Woche</caption>
      <thead>
        <tr>
          <th scope="col">Fachgruppe</th>
          {summen.wochen.map((woche, index) => (
            <th scope="col" key={`${String(woche.isoYear)}-${String(woche.isoWeek)}`}>
              {wochenkopf(woche, summen.wochen[index - 1])}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {summen.fachgruppen.map((zeile) => (
          <tr key={zeile.fachgruppeId} data-zeile="fachgruppe">
            <th scope="row">{zeile.name}</th>
            {zeile.zellen.map((zelle) => (
              <Zelle
                key={`${String(zelle.isoYear)}-${String(zelle.isoWeek)}`}
                anzeige={summenAnzeige(zelle, schwelle)}
              />
            ))}
          </tr>
        ))}
      </tbody>
      <tbody data-zeile="gesamt">
        <tr>
          <th scope="row">
            <strong>Gesamt (intern)</strong>
          </th>
          {summen.gesamt.zellen.map((zelle) => (
            <Zelle
              key={`${String(zelle.isoYear)}-${String(zelle.isoWeek)}`}
              anzeige={summenAnzeige(zelle, schwelle)}
            />
          ))}
        </tr>
      </tbody>
      <tbody data-zeile="extern">
        <tr>
          <th scope="row">
            <em>Externe</em>
          </th>
          {summen.extern.zellen.map((zelle) => (
            <Zelle
              key={`${String(zelle.isoYear)}-${String(zelle.isoWeek)}`}
              anzeige={externAnzeige(zelle, schwelle)}
            />
          ))}
        </tr>
      </tbody>
    </table>
  );
}
